/**
 * Level Test — content layer.
 *
 * These are the DEFAULT (fallback) copy strings. The live source of truth is
 * the backend content store: GET /api/level-test/content returns an override
 * object that is DEEP-MERGED over these defaults at runtime (see
 * mergeLevelTestContent). Admins edit the override via the "Level Test" JSON
 * editor in the admin panel → copy changes WITHOUT a redeploy (important: when
 * Jarvis audio is (re)generated with ElevenLabs, written↔spoken lines drift and
 * must be editable on the fly).
 *
 * VOICE RULE: Jarvis IS Steve Dapper. Always first person ("io/me"), never
 * "il Prof." in the third person — buttons and headers included.
 */

const jarvis = (text, audio = null) => ({ text, audio });

export const LEVEL_TEST_SEGMENTS = [
  { value: 'professional', label: 'Professionista / privato che vuole migliorare il proprio inglese', branch: 'A' },
  { value: 'performer', label: 'Cantante / attore / performer', branch: 'A', upsell: true },
  { value: 'student', label: 'Studente (università, accademia)', branch: 'A', entry: true },
  { value: 'corporate', label: 'HR / manager per un team o azienda', branch: 'B' },
  { value: 'other', label: 'Altro', branch: 'A' },
];

export const LEVEL_TEST_CONTENT = {
  it: {
    meta: {
      pageTitle: 'Scopri il tuo livello — Vocal Fitness',
      brand: 'Vocal Fitness®',
      disclaimer:
        'Il punteggio indica il tuo livello di pronuncia sulla scala Vocal Fitness®, ispirata ai descrittori CEFR. Non è una certificazione CEFR ufficiale.',
    },
    steps: {
      welcome: {
        kicker: 'Test di pronuncia',
        headingLine1: 'L\u2019italiano ha 7 vocali.',
        headingLine2: 'L\u2019inglese britannico ne ha 20.',
        subheading: 'Tredici suoni che la tua bocca non ha mai prodotto.',
        body: [
          'Non che non li conosci: che non li hai mai fatti. I muscoli che servono a produrli non si sono mai mossi in quel modo.',
          'E c\u2019\u00e8 di peggio. Le vocali italiane vivono tutte sui bordi della bocca. Quelle inglesi stanno al centro \u2014 nella zona che tu, parlando italiano, non hai mai usato.',
          'Ecco perch\u00e9 ascoltare non basta. Puoi sentire la differenza e continuare a non riuscire a farla: \u00e8 una questione di muscoli, non di orecchio.',
        ],
        jarvis: jarvis(
          'Ciao, sono il Prof. Steve Dapper. Nei prossimi tre minuti ti faccio pronunciare tre parole inglesi. Non per correggerti l\u2019accento \u2014 per misurare, con l\u2019analisi acustica, dove si posiziona la tua lingua quando le dici. Ti serve solo un posto tranquillo e il microfono acceso. Cominciamo.'
        ),
        cta: 'Scopri quali suoni ti mancano',
        note: '3 minuti \u00b7 non serve registrarsi',
        microcopy: 'Analisi acustica reale. Nessun quiz a risposta multipla: parli tu, e misuro le frequenze della tua voce.',
      },
      mic: {
        jarvis: jarvis(
          'Prima cosa: dammi accesso al microfono. Non registro nulla che resti \u2014 l\u2019audio viene analizzato sul momento e non viene conservato. Quando sei pronto, parliamo.'
        ),
      },
      mirror: {
        title: 'Lo specchio',
        jarvis: jarvis(
          'Se vuoi, accendi la telecamera e guardati mentre parli. Non ti analizzo: ti osservi. \u00c8 il primo passo del metodo \u2014 la propriocezione. Sapere cosa fa la tua bocca mentre la muovi. Se preferisci di no, andiamo avanti lo stesso.'
        ),
        enableCta: 'Accendi la telecamera',
        skipCta: 'Salta questo passo',
        privacy: 'La telecamera resta sul tuo dispositivo. Nessuna immagine viene inviata o salvata.',
      },
      aural: {
        title: 'Il tuo orecchio',
        jarvis: jarvis(
          'Ora mettiamo alla prova il tuo orecchio. Ascolta e dimmi quale suono hai sentito. \u00c8 da qui che nasce una buona pronuncia: prima si sente la differenza, poi la si produce.'
        ),
        prompt: 'Quale suono hai sentito?',
        replay: 'Riascolta',
      },
      isolated: {
        title: 'Il suono isolato',
        jarvis: jarvis(
          'Adesso tre parole. Una alla volta. Le leggi e le dici a voce alta, come le diresti normalmente. Non pensarci troppo: voglio sentire il tuo inglese, non quello che credi che io voglia sentire.'
        ),
        recordCta: 'Registra il suono',
        promptKicker: 'Dì la parola in inglese britannico',
        coldNote: 'Prova prima a orecchio \u2014 ti faccio sentire come la dico io solo se serve.',
        asrDegraded: 'Ho misurato la tua pronuncia, ma in questo momento non ho potuto verificare quale parola hai detto. Il punteggio sui suoni \u00e8 valido.',
        // §2 Errore-che-insegna
        teach: {
          wrongWordHeader: 'Ho sentito un\u2019altra parola',
          wrongWordBody: 'Ho sentito \u00ab{heard}\u00bb, ma cercavamo {target}. Succede: quella grafia in italiano tira da un\u2019altra parte. Ascoltami mentre la dico, guarda la posizione della bocca, e riprova.',
          soundHeader: 'Il suono non \u00e8 quello',
          perPhoneme: {
            'ɔː': 'Labbra arrotondate e spinte in avanti, lingua indietro e in basso, e il suono dura. L\u2019errore italiano \u00e8 farla corta e aperta come in \u201clo\u201d. Questa \u00e8 lunga e rotonda.',
            'ɜː': 'Bocca rilassata, lingua al centro, nessun arrotondamento: \u00e8 un suono neutro e lungo. Non dire \u201cberd\u201d n\u00e9 \u201cburd\u201d all\u2019italiana \u2014 sta a met\u00e0, come un lungo \u201ceeuh\u201d.',
            'æ': 'Bocca aperta pi\u00f9 di quanto ti viene naturale, lingua avanti e in basso. Sta tra la \u201ca\u201d e la \u201ce\u201d italiane, ma non \u00e8 n\u00e9 l\u2019una n\u00e9 l\u2019altra: \u00e8 terra di nessuno per un orecchio italiano.',
          },
          listenBtn: 'Ascoltami',
          retryBtn: 'Riprova',
          remaining: 'Ti restano {n} tentativi su questo suono.',
        },
        // §3 R americana
        rhotic: {
          header: 'Ho sentito la r americana',
          body: 'Non \u00e8 un errore \u2014 \u00e8 un altro inglese. Ma qui stiamo allenando il britannico, e in britannico questa r non si pronuncia: la lingua resta piatta, non si arriccia indietro. Ascoltami e riprova. \u00c8 esattamente la differenza UK\u2194US che stai imparando a sentire.',
        },
        // §4 Non misurato (422)
        unmeasured: {
          header: 'Non ti ho sentito bene',
          body: 'Il segnale era troppo basso o troppo corto per misurarlo. Avvicinati al microfono, riduci il rumore intorno, e tieni il suono per un paio di secondi.',
          notCounted: 'Questa presa non conta come tentativo \u2014 non hai perso nulla.',
        },
        // §5 Momento bidialettale — LAW
        bidialectLaw: {
          opening: 'Aspetta un secondo.',
          body: 'Poco fa ti ho fatto dire LAW all\u2019inglese \u2014 quella britannica. Ma secondo te un americano la dice allo stesso modo? Te lo faccio sentire. Ecco law come la direbbe un londinese\u2026 e come la dice un newyorkese.',
          btnUK: '🇬🇧 Britannico',
          btnUS: '🇺🇸 Americano',
          micro: 'Senti la differenza? Non \u00e8 la stessa vocale. L\u2019inglese non \u00e8 uno solo \u2014 e chi te lo insegna spesso non te lo dice nemmeno.',
          microCompare: '(L\u2019americano lo senti solo per confronto: non entra nel punteggio.)',
          beforeAfter: 'Guarda cos\u2019\u00e8 successo mentre lo dicevi. A freddo eri a {primo}. Dopo avermi ascoltato, {migliore}. In trenta secondi. Immagina in un percorso intero.',
        },
      },
      phrase: {
        title: 'La frase',
        jarvis: jarvis(
          'Ultima cosa: una frase intera. Qui non ti do un voto. Voglio solo sentirti parlare in continuit\u00e0 \u2014 \u00e8 diverso da una parola isolata, e ti serve per capire cosa succede quando i suoni si attaccano tra loro.'
        ),
        recordCta: 'Registra la frase',
      },
      partial: {
        title: 'Primo sguardo',
        jarvis: jarvis(
          'Ho finito di misurare. Ti mostro subito com\u2019\u00e8 andata.'
        ),
        teaserHeading: 'Ho finito di misurare.',
        bodyTemplate: 'Su tre suoni, {n_deboli} sono sotto il livello che serve per suonare naturale in inglese britannico. Il quadro completo \u2014 quali suoni, di quanto sei fuori, e cosa succede alla tua lingua quando li produci \u2014 \u00e8 pronto.',
        unlockCta: 'Sblocca il risultato completo',
      },
      gate: {
        title: 'Dove ti mando il risultato?',
        jarvis: jarvis(
          'Ti serve solo l\u2019email. Il report completo lo vedi subito qui, e te ne mando una copia.'
        ),
        emailLabel: 'Email',
        emailPlaceholder: 'nome@email.com',
        nameLabel: 'Nome (facoltativo)',
        namePlaceholder: 'Nome e cognome',
        companyLabel: 'Nome dell\u2019azienda',
        companyPlaceholder: 'Nome dell\u2019azienda',
        phoneLabel: 'Telefono',
        phonePlaceholder: '+39 ...',
        segmentQuestion: 'Sono:',
        cefrQuestion: 'Hai gi\u00e0 un livello certificato? (facoltativo)',
        cefrOptions: ['Nessuno / non so', 'A1\u2013A2', 'B1', 'B2', 'C1', 'C2'],
        consentPrivacy: 'Ho letto l\u2019informativa privacy e acconsento al trattamento dei miei dati per ricevere il risultato del test.',
        consentPrivacyLinkText: 'Leggi l\u2019informativa',
        consentMarketing: 'Voglio ricevere aggiornamenti e proposte formative da Vocal Fitness. (Facoltativo \u2014 puoi disiscriverti quando vuoi.)',
        consentVersion: 'lt-1.0',
        submitCta: 'Mostrami il risultato',
        errorGeneric: 'Qualcosa \u00e8 andato storto. Riprova.',
      },
      verdict: {
        title: 'Il tuo livello: {livello}',
        subtitle: 'Scala Vocal Fitness®, ispirata ai descrittori CEFR.',
        cefrLabelPrefix: 'Livello Vocal Fitness®',
        opening: 'Ecco cosa ho misurato, suono per suono.',
        callback: 'Ricordi quando ti ho fatto sentire la differenza tra law britannico e americano? Quella non \u00e8 una curiosit\u00e0. \u00c8 il motivo per cui \u201cparlare inglese\u201d non basta: devi sapere quale inglese stai parlando, e allenare i muscoli per quello.',
        bidialectHeading: 'Il tuo accento tra due mondi',
        focusHeading: 'I tre suoni su cui lavorare per primi',
        asyncCefrNote:
          'Se hai gi\u00e0 una certificazione: attesta la tua competenza generale; questa analisi misura la pronuncia nello specifico. Ecco dove i due si incontrano.',
        private: {
          closing: 'Questi tre suoni sono una fotografia. Nell\u2019inglese britannico ci sono 20 vocali, e tu ne hai gi\u00e0 sette in bocca dall\u2019italiano. Restano tredici da costruire. Non da imparare: da costruire, muscolo per muscolo.',
          ctaPrimary: 'Studia con me',
          ctaPrimaryNote: 'Percorsi 1:1 e in piccolo gruppo',
          ctaWhatsapp: 'Scrivimi su WhatsApp',
          ctaSecondary: 'Oppure esplora la libreria fonetica',
        },
        corporate: {
          closing: 'Questa \u00e8 la fotografia di una persona. In un team, lo stesso test racconta dove il gruppo perde comprensibilit\u00e0 nelle call, nelle presentazioni, con i clienti internazionali. Il metodo Vocal Fitness® lavora sulla fisiologia del parlato: non \u201cpi\u00f9 inglese\u201d, ma i muscoli giusti per i suoni che mancano.',
          ctaPrimary: 'Parliamo del tuo team',
          ctaSecondary: 'Scrivimi su WhatsApp',
        },
      },
    },
  },
};

// ---- Deep-merge backend override over defaults (arrays/scalars replaced) ----
const isObj = (v) => v && typeof v === 'object' && !Array.isArray(v);
export const deepMerge = (base, override) => {
  if (!isObj(base) || !isObj(override)) return override === undefined ? base : override;
  const out = { ...base };
  for (const k of Object.keys(override)) {
    out[k] = isObj(base[k]) && isObj(override[k]) ? deepMerge(base[k], override[k]) : override[k];
  }
  return out;
};

export const getLevelTestContent = (lang = 'it') =>
  LEVEL_TEST_CONTENT[lang] || LEVEL_TEST_CONTENT.it;

export const mergeLevelTestContent = (override, lang = 'it') =>
  deepMerge(getLevelTestContent(lang), override || {});
